import React, { useState } from 'react';
import {
  Box,
  Button,
  Text,
  Flex,
  Input,
  VStack,
  Code,
  List,
  ListItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Image,
  Badge,
  Spinner,
} from '@chakra-ui/react';
import { BsKey, BsShield } from 'react-icons/bs';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { FiRefreshCw } from 'react-icons/fi';
import API from '../helpers/axiosInstance';

// 2FA Setup Modal Component
export function TwoFactorAuthModal({ isOpen, onClose }) {
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // API data states
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [manualEntryKey, setManualEntryKey] = useState('');
  const [otpauthUrl, setOtpauthUrl] = useState('');

  // API headers helper
  const getApiHeaders = () => ({
    "x-api-key": localStorage.getItem("api-key"),
    "x-access-token": localStorage.getItem("customer-portal"),

  });

  // Fetch 2FA setup data when modal opens
  React.useEffect(() => {
    if (isOpen) {
      fetchTwoFactorSetup();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchTwoFactorSetup = async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/customer-portal/me/2fa/setup`;
      const response = await fetch(url, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({})
      });

      if (!response.ok) {
        throw new Error('Failed to fetch 2FA setup');
      }

      const data = await response.json();

      // Set the data from API response
      setQrCodeUrl(data.qr_png_base64);
      setManualEntryKey(data.manual_key);
      setOtpauthUrl(data.otpauth_url);
    } catch (error) {
      console.error('Error fetching 2FA setup:', error);
      // Optionally show error toast here
    } finally {
      setLoading(false);
    }
  };

  // Helper function to extract email from otpauth URL
  const extractEmailFromOtpauth = (url) => {
    try {
      const match = url.match(/xNotify:([^?]+)/);
      return match ? `xNotify (${match[1]})` : 'xNotify';
    } catch {
      return 'xNotify';
    }
  };

  const handleVerifyCode = async () => {
    setVerifying(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/customer-portal/me/2fa/enable`;
      const response = await API.post(url, { code: verificationCode }, { headers: getApiHeaders() });

      const data = response.data;

      // // Update localStorage with new values
      localStorage.setItem('customer-portal', data.accessToken);
      localStorage.setItem('mfa_enabled', data.mfa_enabled);
      localStorage.setItem('mfa_verified', data.mfa_verified);

    

      // Close modal
      onClose();
      window.location.reload()

    } catch (error) {
      console.error('Error verifying code:', error);
    } finally {
      setVerifying(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Two-Factor Authentication Setup</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3} align="stretch">
            {/* Step 1: QR Code */}
            <Box textAlign="center">
              <Badge
                bg="var(--brand-primary)"
                color="white"
                py={2}
                px={4}
                borderRadius="lg"
                mb={3}
                display="inline-flex"
                alignItems="center"
                gap={2}
              >
                <BsShield size={16} />
                <Text fontSize="sm">Step 1: Scan QR Code</Text>
              </Badge>

              {/* QR Code */}
              <Box
                bg="white"
                border="2px"
                borderColor="gray.200"
                borderRadius="lg"
                p={3}
                mx="auto"
                width="fit-content"
              >
                {loading ? (
                  <Flex width="150px" height="150px" alignItems="center" justifyContent="center">
                    <Spinner size="xl" color="var(--brand-primary)" />
                  </Flex>
                ) : qrCodeUrl ? (
                  <Image
                    src={qrCodeUrl}
                    alt="2FA QR Code"
                    width="150px"
                    height="150px"
                  />
                ) : (
                  <Box width="150px" height="150px" bg="gray.100" />
                )}
              </Box>

              <Text fontSize="xs" color="gray.600" mt={2}>
                Open Google Authenticator and scan this QR code
              </Text>
            </Box>

            {/* Step 2: Manual Entry Option */}
            <Box
              bg="gray.50"
              border="1px"
              borderColor="gray.200"
              borderRadius="lg"
              p={4}
            >
              <Flex alignItems="center" gap={2} mb={2}>
                <BsKey size={16} color="gray.600" />
                <Text fontSize="sm">Or enter this key manually:</Text>
              </Flex>
              <Box
                bg="white"
                border="1px"
                borderColor="gray.300"
                borderRadius="md"
                p={2}
                textAlign="center"
              >
                <Code fontSize="sm" fontFamily="mono" userSelect="all">
                  {loading ? 'Loading...' : manualEntryKey || 'Loading...'}
                </Code>
              </Box>
              <Text fontSize="xs" color="gray.500" mt={2}>
                Account: {loading ? 'Loading...' : otpauthUrl ? extractEmailFromOtpauth(otpauthUrl) : 'xNotify'}
              </Text>
            </Box>

            {/* Step 3: Verification Code Input */}
            <VStack spacing={3} align="stretch">
              <Badge
                bg="var(--brand-primary)"
                color="white"
                py={2}
                px={4}
                borderRadius="lg"
                display="inline-flex"
                alignItems="center"
                gap={2}
                width="fit-content"
              >
                <CheckCircleIcon size={16} />
                <Text fontSize="sm">Step 2: Enter Verification Code</Text>
              </Badge>

              <Box>
                <Text fontSize="sm" mb={2}>
                  6-Digit Code from Google Authenticator
                </Text>
                <Input
                  placeholder="000000"
                  value={verificationCode}
                  onChange={handleCodeChange}
                  textAlign="center"
                  fontSize="lg"
                  letterSpacing="wider"
                  fontFamily="mono"
                  maxLength={6}
                />
                <Text fontSize="xs" color="gray.500" mt={2}>
                  Enter the 6-digit code shown in your Google Authenticator app
                </Text>
              </Box>
            </VStack>

            {/* Information Box */}
            <Box
              bg="blue.50"
              border="1px"
              borderColor="blue.200"
              borderRadius="lg"
              p={4}
            >
              <Flex alignItems="center" gap={2} mb={2}>
                <FiRefreshCw size={16} color="var(--brand-primary)" />
                <Text fontSize="sm" fontWeight="semibold">
                  Important Information
                </Text>
              </Flex>
              <List spacing={1} fontSize="xs" color="gray.600" ml={6}>
                <ListItem>
                  Download Google Authenticator from your app store if you haven't already
                </ListItem>
                <ListItem>
                  The code refreshes every 30 seconds
                </ListItem>
                <ListItem>
                  Keep your phone with you - you'll need it for future logins
                </ListItem>
                
              </List>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="outline" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            bg="var(--brand-primary)"
            color="white"
            _hover={{ bg: 'var(--brand-primary-hover)' }}
            onClick={handleVerifyCode}
            isDisabled={verificationCode.length !== 6 || verifying}
            isLoading={verifying}
            loadingText="Verifying..."
            leftIcon={!verifying && <CheckCircleIcon size={16} />}
          >
            Verify & Enable 2FA
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

